import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ProductsHero from './components/ProductsHero';
import ProductCard from './components/ProductCard';
import TechSpecs from './components/TechSpecs';
import QualitySection from './components/QualitySection';
import ProductsCTA from './components/ProductsCTA';

const ProductsPage = () => {
  const [refProducts, inViewProducts] = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });
  const [refProcess, inViewProcess] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [refCompare, inViewCompare] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [activeFilter, setActiveFilter] = useState('all');
  const [hoveredProduct, setHoveredProduct] = useState<number | null>(null);

  const filters = [
    { id: 'all', label: 'Tümü', icon: 'ri-apps-line' },
    { id: 'pet-sise', label: 'PET Şişe', icon: 'ri-drop-line' },
    { id: 'pet-kavanoz', label: 'PET Kavanoz', icon: 'ri-archive-line' },
    { id: 'pet-preform', label: 'PET Preform', icon: 'ri-flask-line' },
    { id: 'pp-kapak', label: 'PP Kapak', icon: 'ri-shield-check-line' },
  ];

  const products = [
    {
      id: 'pet-sise',
      category: 'PET BOTTLE',
      name: 'PET Şişe',
      description:
        'AnadoluPet PET Şişeleri, yüksek berraklık, dayanıklılık ve gıda temasına uygunluk sağlayan hammaddelerle üretilmektedir. Farklı hacim, ağız tipi ve boyun seçenekleri ile hem içecek hem de endüstriyel kullanım için esnek çözümler sunar.',
      image:
        'https://public.readdy.ai/ai/img_res/edited_719abb1687cbf4e299a58b1d7bf36eb8_59ed2a87.jpg',
      features: ['Yüksek Berraklık', 'Üstün Dayanıklılık', 'Gıda Uygunluğu', 'Çeşitli Hacimler'],
      applications: ['Sıvı Gıda', 'İçecek', 'Temizlik Ürünleri', 'Kimyasal Ürünler'],
      icon: 'ri-drop-line',
    },
    {
      id: 'pet-kavanoz',
      category: 'PET JAR',
      name: 'PET Kavanoz',
      description:
        'PET Kavanozlarımız, ürünlerin tazeliğini ve güvenliğini koruyan, hafif ama yüksek mukavemetli ambalaj çözümleri sunar. Şeffaf yapısı sayesinde raflarda ürün görünürlüğünü artırır.',
      image:
        'https://public.readdy.ai/ai/img_res/8160ae49312274cb54bc8e6ec2d92c97.jpg',
      features: ['Tazelik Koruma', 'Hafif ve Mukavemetli', 'Yüksek Görünürlük', 'Geniş Ağız'],
      applications: ['Bal', 'Reçel', 'Kuruyemiş', 'Sos', 'Kozmetik'],
      icon: 'ri-archive-line',
    },
    {
      id: 'pet-preform',
      category: 'PET PREFORM',
      name: 'PET Preform',
      description:
        'AnadoluPet PET Preformları, homojen yapı ve hassas ölçülerle üretilerek şişirme makinelerinde mükemmel performans sağlar. Yüksek üretim verimliliği ve tutarlı kalite.',
      image:
        'https://public.readdy.ai/ai/img_res/edited_c0ec483edae3c6d3a589413816c78409_72c72c0b.jpg',
      features: ['Homojen Yapı', 'Hassas Ölçüler', 'Mükemmel Performans', 'Yüksek Verimlilik'],
      applications: ['Şişirme Makineleri', 'PET Şişe Üretimi', 'Endüstriyel Üretim'],
      icon: 'ri-flask-line',
    },
    {
      id: 'pp-kapak',
      category: 'PP LID',
      name: 'PP Kapak',
      description:
        'PP Kapaklarımız, PET şişe ve kavanozlarla tam uyumlu olacak şekilde sızdırmazlık ve dayanıklılık sağlayacak biçimde tasarlanır. Çeşitli renk ve boyut seçenekleri mevcuttur.',
      image:
        'https://readdy.ai/api/search-image?query=assortment%20of%20polypropylene%20plastic%20bottle%20caps%20closures%20in%20white%20and%20teal%20colors%20neatly%20arranged%20flat%20lay%20on%20white%20surface%20macro%20product%20photography%20showing%20tamper%20evident%20bands%20and%20thread%20details%20packaging%20components%20studio%20lighting%20sharp%20focus&width=420&height=280&seq=pp-cap-carousel-2025-d4&orientation=landscape',
      features: ['Tam Uyumluluk', 'Sızdırmazlık', 'Yüksek Dayanıklılık', 'Renk Çeşitliliği'],
      applications: ['PET Şişe Kapatma', 'PET Kavanoz Kapatma', 'Sızdırmaz Sistemler'],
      icon: 'ri-shield-check-line',
    },
  ];

  const filteredProducts =
    activeFilter === 'all' ? products : products.filter((p) => p.id === activeFilter);

  const processSteps = [
    {
      step: '01',
      title: 'Hammadde Seçimi',
      desc: 'Gıda temasına uygun, sertifikalı malzeme tedariği',
      icon: 'ri-seedling-line',
    },
    {
      step: '02',
      title: 'Kalıp Tasarımı',
      desc: 'CNC kalıp atölyesinde özel tasarım üretimi',
      icon: 'ri-pencil-ruler-2-line',
    },
    {
      step: '03',
      title: 'Üretim',
      desc: 'Modern makinelerle hassas ve hızlı üretim',
      icon: 'ri-settings-3-line',
    },
    {
      step: '04',
      title: 'Kalite Kontrol',
      desc: 'Laboratuvar testleri ve detaylı analiz',
      icon: 'ri-search-eye-line',
    },
    {
      step: '05',
      title: 'Paketleme',
      desc: 'Hijyenik ortamda güvenli paketleme',
      icon: 'ri-box-3-line',
    },
    {
      step: '06',
      title: 'Teslimat',
      desc: 'Hızlı ve güvenilir lojistik',
      icon: 'ri-truck-line',
    },
  ];

  const compareData = [
    { feature: 'Berraklık', pet: true, cam: true, hdpe: false },
    { feature: 'Hafiflik', pet: true, cam: false, hdpe: true },
    { feature: 'Kırılmazlık', pet: true, cam: false, hdpe: true },
    { feature: 'Geri Dönüşüm', pet: true, cam: true, hdpe: true },
    { feature: 'Gıda Uygunluğu', pet: true, cam: true, hdpe: true },
    { feature: 'Maliyet Avantajı', pet: true, cam: false, hdpe: true },
    { feature: 'Tasarım Esnekliği', pet: true, cam: false, hdpe: false },
    { feature: 'Bariyer Özelliği', pet: true, cam: true, hdpe: false },
  ];

  return (
    <div className="min-h-screen bg-white font-poppins">
      <Navbar />

      {/* Hero */}
      <ProductsHero />

      {/* Filter Bar */}
      <section className="sticky top-[60px] sm:top-[68px] z-40 bg-white/95 backdrop-blur-lg border-b border-gray-100 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 sm:gap-3 py-3 sm:py-4 overflow-x-auto scrollbar-hide">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-400 whitespace-nowrap cursor-pointer ${
                  activeFilter === filter.id
                    ? 'bg-gradient-to-r from-green to-teal text-white shadow-md shadow-green/20'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-100'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${filter.icon} text-sm`} />
                </div>
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section
        ref={refProducts}
        id="urunler"
        className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-white to-gray-50/50"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewProducts ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 sm:mb-14 lg:mb-18"
          >
            <motion.span
              initial={{ opacity: 0, letterSpacing: '0.05em' }}
              animate={inViewProducts ? { opacity: 1, letterSpacing: '0.2em' } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-teal text-xs sm:text-sm font-semibold uppercase inline-block"
            >
              /ÜRÜN KATALOĞU
            </motion.span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-4 sm:mb-6">
              Ambalaj Çözümlerimiz
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Gıda ve endüstriyel sektörlere özel, yüksek kalite standartlarında üretim
            </p>
          </motion.div>

          {/* Products */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10"
              data-product-shop
            >
              {filteredProducts.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  inView={inViewProducts}
                  isActive={hoveredProduct === index}
                  onHover={() => setHoveredProduct(index)}
                />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Production Process */}
      <section
        ref={refProcess}
        className="py-16 sm:py-20 lg:py-28 bg-white relative overflow-hidden"
      >
        <motion.div
          animate={{ x: [0, -20, 0], y: [0, 15, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-20 right-10 w-80 h-80 bg-green/5 rounded-full blur-3xl pointer-events-none"
        />

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewProcess ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 sm:mb-16 lg:mb-20"
          >
            <span className="text-teal text-xs sm:text-sm font-semibold uppercase tracking-wider">
              /ÜRETİM SÜRECİ
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-4 sm:mb-6">
              Üretimden Teslimat&apos;a
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Her aşamada titizlik, her üründe mükemmellik
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {processSteps.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={inViewProcess ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="group relative"
              >
                <div className="relative bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 hover:border-teal/30 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-default overflow-hidden">
                  {/* Step Number Background */}
                  <div className="absolute top-4 right-4 text-6xl sm:text-7xl font-bold text-gray-50 group-hover:text-teal/8 transition-colors duration-500 select-none leading-none">
                    {item.step}
                  </div>

                  {/* Hover Shimmer */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <motion.div
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2 }}
                      className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-teal/5 to-transparent skew-x-12"
                    />
                  </div>

                  <div className="relative">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 8 }}
                      transition={{ duration: 0.4 }}
                      className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-gradient-to-br from-teal to-green rounded-xl text-white mb-5 shadow-md"
                    >
                      <i className={`${item.icon} text-xl sm:text-2xl`} />
                    </motion.div>

                    <div className="text-teal text-xs font-bold mb-2 tracking-wider">
                      ADIM {item.step}
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-navy mb-2 group-hover:text-teal transition-colors duration-300">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>

                  {/* Bottom Accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal via-green to-teal scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Specs */}
      <TechSpecs />

      {/* Comparison Table */}
      <section
        ref={refCompare}
        className="py-16 sm:py-20 lg:py-28 bg-white relative overflow-hidden"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inViewCompare ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 sm:mb-16"
          >
            <span className="text-teal text-xs sm:text-sm font-semibold uppercase tracking-wider">
              /KARŞILAŞTIRMA
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-4 sm:mb-6">
              Neden PET Ambalaj?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              PET ambalajın diğer malzemelere göre avantajları
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inViewCompare ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="bg-white rounded-2xl lg:rounded-3xl shadow-xl border border-gray-100 overflow-hidden"
          >
            {/* Table Header */}
            <div className="grid grid-cols-4 bg-gradient-to-r from-navy to-navy/95">
              <div className="p-4 sm:p-6 text-white/70 text-xs sm:text-sm font-semibold uppercase tracking-wider">
                Özellik
              </div>
              <div className="p-4 sm:p-6 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green/20 rounded-full">
                  <i className="ri-drop-line text-green text-sm" />
                  <span className="text-green text-xs sm:text-sm font-bold">PET</span>
                </div>
              </div>
              <div className="p-4 sm:p-6 text-center">
                <span className="text-white/60 text-xs sm:text-sm font-medium">Cam</span>
              </div>
              <div className="p-4 sm:p-6 text-center">
                <span className="text-white/60 text-xs sm:text-sm font-medium">HDPE</span>
              </div>
            </div>

            {/* Table Body */}
            {compareData.map((row, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={inViewCompare ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.06 }}
                className={`grid grid-cols-4 items-center border-b border-gray-50 last:border-b-0 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                } hover:bg-teal/5 transition-colors duration-300`}
              >
                <div className="p-4 sm:p-5 text-navy text-xs sm:text-sm font-medium">
                  {row.feature}
                </div>
                <div className="p-4 sm:p-5 text-center">
                  {row.pet ? (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-green/10 rounded-full mx-auto">
                      <i className="ri-check-line text-green text-sm sm:text-base" />
                    </div>
                  ) : (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-red-50 rounded-full mx-auto">
                      <i className="ri-close-line text-red-400 text-sm sm:text-base" />
                    </div>
                  )}
                </div>
                <div className="p-4 sm:p-5 text-center">
                  {row.cam ? (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-green/10 rounded-full mx-auto">
                      <i className="ri-check-line text-green text-sm sm:text-base" />
                    </div>
                  ) : (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-red-50 rounded-full mx-auto">
                      <i className="ri-close-line text-red-400 text-sm sm:text-base" />
                    </div>
                  )}
                </div>
                <div className="p-4 sm:p-5 text-center">
                  {row.hdpe ? (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-green/10 rounded-full mx-auto">
                      <i className="ri-check-line text-green text-sm sm:text-base" />
                    </div>
                  ) : (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-red-50 rounded-full mx-auto">
                      <i className="ri-close-line text-red-400 text-sm sm:text-base" />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Table Footer */}
            <div className="grid grid-cols-4 bg-gradient-to-r from-green/5 to-teal/5 border-t border-green/20">
              <div className="p-4 sm:p-5 text-navy text-xs sm:text-sm font-bold">Toplam Avantaj</div>
              <div className="p-4 sm:p-5 text-center">
                <span className="text-green text-lg sm:text-xl font-bold">8/8</span>
              </div>
              <div className="p-4 sm:p-5 text-center">
                <span className="text-gray-500 text-lg sm:text-xl font-bold">4/8</span>
              </div>
              <div className="p-4 sm:p-5 text-center">
                <span className="text-gray-500 text-lg sm:text-xl font-bold">4/8</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quality Section */}
      <QualitySection />

      {/* CTA */}
      <ProductsCTA />

      <Footer />
    </div>
  );
};

export default ProductsPage;
